/**
 * Opening a search hit (palette, Home fallback, case-not-found page). The API gives each hit a Patchbay route
 * (`/case/C-#####`, `/admin/people`) or, with `external`, an https URL in Desk or Conduit; a hit with no href has
 * nowhere real to go and renders as plain text. Internal hrefs still pass safeReturnTo.
 */
import { Link } from '@tanstack/react-router'
import type * as React from 'react'
import type { SearchHit } from './api/generated'
import { safeReturnTo } from './safeUrl'

export type HitTarget = { kind: 'route'; href: string } | { kind: 'external'; href: string } | { kind: 'none' }

export function hitTarget(hit: Pick<SearchHit, 'href' | 'external'>): HitTarget {
  if (!hit.href) return { kind: 'none' }
  if (hit.external) {
    try {
      const url = new URL(hit.href)
      return url.protocol === 'https:' && !url.username && !url.password ? { kind: 'external', href: url.href } : { kind: 'none' }
    } catch {
      return { kind: 'none' }
    }
  }
  const href = safeReturnTo(hit.href)
  return href === '/' && hit.href !== '/' ? { kind: 'none' } : { kind: 'route', href }
}

/** Follow a hit: a route navigates in the SPA, an external URL opens in a new tab. */
export function openSearchHit(hit: Pick<SearchHit, 'href' | 'external'>, navigate: (opts: { href: string }) => unknown): void {
  const t = hitTarget(hit)
  if (t.kind === 'route') void navigate({ href: t.href })
  else if (t.kind === 'external') window.open(t.href, '_blank', 'noopener,noreferrer')
}

export function SearchHitLink({ hit, className, children }: { hit: SearchHit; className?: string; children: React.ReactNode }) {
  const t = hitTarget(hit)
  if (t.kind === 'route')
    return (
      <Link to={t.href} className={className}>
        {children}
      </Link>
    )
  if (t.kind === 'external')
    return (
      <a href={t.href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    )
  return <span className={className}>{children}</span>
}
